import * as Sentry from '@sentry/react'
import { Aura } from 'auragin'
import { validateDownloadedModules } from '../../versions/validate-downloaded'
import { IDiscoveryResponse } from '../../../interfaces/MFE/IDiscoveryAPI'
import initializeOneSignal from '../initializers/initializeOneSignal'
import { onForegroundNotification } from '../../oneSignal'
import { getInitTracker } from '../tracking/init-tracker'

export default async function initMobile (microfrontendsResponse: IDiscoveryResponse): Promise<void> {
  const tracker = getInitTracker()

  // Phase 1: Extract modules + initialize plugin (parallel, no server dependency)
  const [validateResult, auraInitResult] = await Promise.allSettled([
    tracker.trackStep('mobile_validate_modules', async () => await validateDownloadedModules(microfrontendsResponse), {
      modulesCount: microfrontendsResponse.microFrontends.length,
      moduleIds: microfrontendsResponse.microFrontends.map(m => m.mfe_id)
    }),
    tracker.trackStep('mobile_aura_initialize', async () => await Aura.initialize())
  ])

  // Reportar resultados individuales de Phase 1 si fallaron silenciosamente
  if (validateResult.status === 'rejected') {
    Sentry.addBreadcrumb({
      category: 'init.mobile',
      message: '[mobile] validateDownloadedModules rejected in allSettled',
      level: 'warning',
      data: { error: validateResult.reason?.message ?? String(validateResult.reason) }
    })
  }

  if (auraInitResult.status === 'rejected') {
    Sentry.addBreadcrumb({
      category: 'init.mobile',
      message: '[mobile] Aura.initialize() rejected in allSettled',
      level: 'warning',
      data: { error: auraInitResult.reason?.message ?? String(auraInitResult.reason) }
    })
  }

  // Phase 2: Start web server AFTER extraction is complete
  await tracker.trackStep('mobile_ws_start', async () => {
    await Aura.wsStart({ port: 9001 }).catch((error) => {
      const msg = error?.message?.toLowerCase() ?? ''
      if (msg.includes('already running') || msg.includes('server is running')) {
        Sentry.addBreadcrumb({
          category: 'init.mobile',
          message: '[mobile] Web server already running (reused from previous lifecycle)',
          level: 'info'
        })
        return
      }
      throw error
    })
  })

  // Phase 3: Post-init setup
  tracker.trackStepSync('mobile_onesignal', () => {
    initializeOneSignal()
    onForegroundNotification((type, data) => {
      window.dispatchEvent(new CustomEvent('ONESIGNAL_FOREGROUND', { detail: { type, data } }))
    })
  })
}
